import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  Skeleton,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useState } from 'react';
import {
  DotsThreeVertical,
  PencilSimple,
  TrashSimple,
} from '@phosphor-icons/react';
import NoDataFallback from '../common/NoDataFallback';
import DeleteModal from '../modals/DeleteModal';

interface BrandsProps {
  brandsList: any[];
  brandsListLoading?: boolean;
  onEditBrand: (brand: any) => void;
  onBrandSelect?: (brand: any) => void;
  onDeleteBrand: (brand: any) => Promise<void>;
  brandsListError?: any;
  isBrandsListError?: boolean;
  deletePending: boolean;
}

const Brands = ({
  brandsList,
  brandsListLoading,
  onEditBrand,
  onBrandSelect,
  onDeleteBrand,
  brandsListError,
  isBrandsListError,
  deletePending,
}: BrandsProps) => {
  // States for menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedBrand, setSelectedBrand] = useState<any>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });
  // Menu handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, brand: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedBrand(brand);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Handle edit
  const handleEdit = () => {
    if (selectedBrand) {
      onEditBrand(selectedBrand);
      handleMenuClose();
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (selectedBrand?.id) {
      try {
        await onDeleteBrand(selectedBrand);
        handleMenuClose();
        setDeleteModalOpen(false);
      } catch (error) {
        console.error('Error in handleDelete:', error);
      }
    }
  };

  // Handle brand click
  const handleBrandClick = (brand: any) => {
    if (onBrandSelect) {
      onBrandSelect(brand);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1.5,
      headerClassName: 'data-grid-header',
      align: 'left',
      headerAlign: 'left',
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            color: 'primary.main',
            cursor: 'pointer',
            '&:hover': {
              textDecoration: 'underline',
            },
            width: '100%',
            display: 'flex',
            alignItems: 'center',
          }}
          onClick={() => handleBrandClick(params.row)}
        >
          <Tooltip title="View Details" placement="top">
            <Typography
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
                fontSize: '16px',
                fontWeight: 400,
                lineHeight: '100%',
                letterSpacing: '-1%',
              }}
            >
              {params.value}
            </Typography>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: 'poc_at_brand',
      headerName: 'Contact Person',
      flex: 2,
      headerClassName: 'data-grid-header',
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          sx={{
            color: 'custom.textBlue',
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '100%',
            letterSpacing: '-1%',
          }}
        >
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'poc_at_email',
      headerName: 'Contact Email',
      flex: 2.5,
      headerClassName: 'data-grid-header',
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          sx={{
            color: 'custom.textBlue',
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '100%',
            letterSpacing: '-1%',
          }}
        >
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'poc_role',
      headerName: 'Role',
      flex: 1,
      headerClassName: 'data-grid-header',
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          sx={{
            color: 'custom.textBlue',
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '100%',
            letterSpacing: '-1%',
          }}
        >
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'poc_contact',
      headerName: 'Contact Number',
      flex: 1.5,
      headerClassName: 'data-grid-header',
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          sx={{
            color: 'custom.textBlue',
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '100%',
            letterSpacing: '-1%',
          }}
        >
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Action',
      flex: 0.8,
      headerClassName: 'data-grid-header',
      headerAlign: 'right',
      align: 'right',
      renderCell: (params: GridRenderCellParams) => (
        <IconButton onClick={e => handleMenuOpen(e, params.row)} size="small">
          <DotsThreeVertical size={16} />
        </IconButton>
      ),
    },
  ];

  // Loading Skeleton Component
  const LoadingSkeleton = () => (
    <Box sx={{ width: '100%', height: '100%' }}>
      {[...Array(5)].map((_, index) => (
        <Box
          key={index}
          sx={{
            display: 'grid',
            gridTemplateColumns: '1.5fr 2fr 2.5fr 1fr 1.5fr 0.8fr',
            gap: 2,
            p: 2,
            borderBottom: '1px solid rgba(224, 224, 224, 1)',
            alignItems: 'center',
          }}
        >
          <Skeleton variant="text" width="80%" height={24} />
          <Skeleton variant="text" width="70%" height={24} />
          <Skeleton variant="text" width="85%" height={24} />
          <Skeleton variant="text" width="60%" height={24} />
          <Skeleton variant="text" width="75%" height={24} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Skeleton variant="circular" width={24} height={24} />
          </Box>
        </Box>
      ))}
    </Box>
  );

  // Check for loading state first
  if (brandsListLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            border: '1px solid rgba(224, 224, 224, 1)',
            borderRadius: '8px',
            overflow: 'hidden',
            position: 'relative',
            height: 'calc(100vh - 200px)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              overflow: 'auto',
            }}
          >
            <Box
              sx={{
                bgcolor: '#F8F9FC',
                display: 'grid',
                gridTemplateColumns: '1.5fr 2fr 2.5fr 1fr 1.5fr 0.8fr',
                gap: 2,
                p: 2,
                borderBottom: '1px solid rgba(224, 224, 224, 1)',
              }}
            >
              <Typography
                sx={{
                  color: 'custom.textBlue',
                  fontSize: '14px',
                  fontWeight: 600,
                }}
              >
                Name
              </Typography>
              <Typography
                sx={{
                  color: 'custom.textBlue',
                  fontSize: '14px',
                  fontWeight: 600,
                }}
              >
                Contact Person
              </Typography>
              <Typography
                sx={{
                  color: 'custom.textBlue',
                  fontSize: '14px',
                  fontWeight: 600,
                }}
              >
                Contact Email
              </Typography>
              <Typography
                sx={{
                  color: 'custom.textBlue',
                  fontSize: '14px',
                  fontWeight: 600,
                }}
              >
                Role
              </Typography>
              <Typography
                sx={{
                  color: 'custom.textBlue',
                  fontSize: '14px',
                  fontWeight: 600,
                }}
              >
                Contact Number
              </Typography>
              <Typography
                sx={{
                  color: 'custom.textBlue',
                  fontSize: '14px',
                  fontWeight: 600,
                  textAlign: 'right',
                }}
              >
                Action
              </Typography>
            </Box>
            <LoadingSkeleton />
          </Box>
        </Box>
      </Box>
    );
  }

  // Then check for error
  if (isBrandsListError && brandsListError) {
    return <NoDataFallback httpStatusCode={brandsListError?.status} />;
  }

  // Finally check for empty list
  if (!brandsList || brandsList?.length === 0) {
    return (
      <NoDataFallback
        message="No brands available"
        description="Please create a brand first to add or see brands"
      />
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          border: '1px solid rgba(224, 224, 224, 1)',
          borderRadius: '8px',
          overflow: 'hidden',
          position: 'relative',
          height: 'calc(100vh - 200px)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <DataGrid
          rows={brandsList || []}
          columns={columns}
          disableRowSelectionOnClick
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50, 75, 100]}
          sx={{
            flex: 1,
            '& .data-grid-header': {
              bgcolor: '#F8F9FC',
              color: 'custom.textBlue',
              fontSize: '14px',
              fontWeight: 600,
              padding: '12px 16px',
            },
            '& .MuiDataGrid-cell': {
              padding: '12px 16px',
              fontSize: '14px',
              display: 'flex',
              color: 'red',
              alignItems: 'center',
              '&:focus, &:focus-within': {
                outline: 'none !important',
              },
            },
            border: 'none',
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid rgba(224, 224, 224, 1)',
            },
          }}
          slots={{
            noRowsOverlay: () => (
              <Box sx={{ p: 2, textAlign: 'center' }}>No brands available</Box>
            ),
          }}
        />
      </Box>

      {/* Menu component */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
            borderRadius: '8px',
            mt: 1,
          },
        }}
      >
        <MenuItem
          onClick={handleEdit}
          sx={{
            fontSize: '14px',
            gap: 1,
            py: 1,
            px: 2,
          }}
        >
          <PencilSimple size={16} />
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => setDeleteModalOpen(true)}
          sx={{
            fontSize: '14px',
            gap: 1,
            py: 1,
            px: 2,
            color: '#E74C3C',
          }}
        >
          <TrashSimple size={16} />
          Delete
        </MenuItem>
      </Menu>
      <DeleteModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onDelete={handleDelete}
        isPending={deletePending}
      />
    </Box>
  );
};

export default Brands;
